/**
 * Shared utility functions for activity formatting
 */

/**
 * Maps activity action types to human-readable labels
 * @param {string} action - The action type from the activity
 * @returns {string} Human-readable label
 */
export const getActionLabel = (action) => {
  const labels = {
    login: 'Logged In',
    logout: 'Logged Out',
    login_failed: 'Failed Login',
    created: 'Created',
    updated: 'Updated',
    deleted: 'Deleted',
    answered: 'Answered Question',
    sent_discussion_message: 'Sent Discussion Message',
    sent_ai_chat_message: 'Sent AI Message',
  };

  return labels[action] || action.split('_').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

/**
 * Maps activity actions to badge color variants
 * @param {string} action - The action type from the activity
 * @returns {string} Badge variant name
 */
export const getActionBadgeVariant = (action) => {
  if (action === 'created') return 'success';
  if (action === 'deleted') return 'danger';
  if (action === 'login') return 'primary';
  if (action === 'login_failed') return 'warning';
  if (action === 'logout') return 'gray';
  return 'gray';
};

/**
 * Converts resource type to human-readable label
 * @param {string} resourceType - The resource type from activity
 * @returns {string} Human-readable label
 */
export const getResourceTypeLabel = (resourceType) => {
  const labels = {
    conversations: 'Conversation',
    collections: 'Collection',
    items: 'Item',
    products: 'Product',
    users: 'User',
    questions: 'Question',
    login_sessions: 'Login Session',
  };

  return labels[resourceType] || resourceType?.split('_').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ') || 'Unknown';
};

/**
 * Formats ISO timestamp to relative time
 * @param {string} timestamp - ISO timestamp string
 * @returns {string} Relative time string
 */
export const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};
